import { useContext, useEffect, useState } from 'react';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import { useRouter } from 'next/router';
import { GetServerSidePropsContext } from 'next';
import { renderToString } from 'react-dom/server';

import {
  BlogPostType,
  ContextualCardType,
  TableOfContentType,
} from 'src/types';
import AppLayout from '@layouts/AppLayout';

import { fetchPostDetails } from '@lib/API/blogAPI';
import {
  calculateReadingTime,
  generateBreadcrumbsFromUrl,
  getAssetUrl,
  generateTOC,
} from '@utils/common.utils';
import { StateContext } from '@store/store';
import HeaderWithCTA from '@components/header-with-cta';
import BreadCrumb from '@components/breadcrumbs';
import ImageComponent from '@components/common/ImageComponent/ImageComponent';
import BlogTags from '@components/tag/blog-tags';
import TableOfContent from '@components/table-of-content';
import ShareOptionsWrapper from '@components/share-options-wrapper';
import Banner from '@components/banner';
import YouMayAlsoLike from '@components/you-may-also-like';
import AuthorDetails from '@components/authorDetails';
import BlogPostSEO from '@components/SEO/BlogPostSEO';
import BlogBody from '@components/blog-posts/blog-body/BlogBody';
import ContextualCard from '@components/contextual-card';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import { getImageSize } from '@utils/getImageSize';
import useBlogAppLoad from '@hooks/useBlogAppLoad';
import { DEFAULT_LOGO_URL } from '@constants/common';
import useSharedState from '@invideoio/web-shared/hooks/useSharedState';
import ErrorBoundary from '@components/ErrorBoundary';
import useTranslation from 'next-translate/useTranslation';

const FEATURE_IMAGEKIT_MAP = {
  [PlatformType.Desktop]: { height: 600, dpr: 1 },
  [PlatformType.Tablet]: { height: 452, dpr: 1 },
  [PlatformType.Mobile]: { height: 184, dpr: 1 },
  default: { height: 600, dpr: 1 },
};
interface PostType {
  article: BlogPostType;
}

const BlogPost = ({ article }: PostType) => {
  const globalState = useContext(StateContext);
  const { platform, browser } = globalState;
  const router = useRouter();

  const { state } = useSharedState();

  const { t, lang } = useTranslation('common');

  const pagePathName = `${lang === 'en' ? '' : '/' + lang}/blog/${
    router.query.post
  }/`;

  const host_url = process.env.NEXT_PUBLIC_BASE_HOST || 'https://invideo.io';
  const tagTextClass = 'sm:iv-text-body-xl iv-text-body-2xl';

  const [hasUserLoggedIn, setHasUserLoggedIn] = useState<Boolean>(false);
  const [toc, setTOC] = useState<TableOfContentType[]>([]);

  useBlogAppLoad();

  useEffect(() => {
    if (state.user) {
      setHasUserLoggedIn(true);
    }
  }, [state.user]);

  useEffect(() => {
    if (article) {
      const newTOC = generateTOC(article.blogBody);
      setTOC(newTOC);
    }
  }, [article]);

  if (!article) {
    return null;
  }

  const {
    slug,
    title,
    featureImage,
    author,
    publishedAt,
    blogBody,
    platform: blogPlatform,
    category: blogCategory,
    profession: blogProfession,
    contextualCard: contextualCards,
  } = article;

  return (
    <AppLayout platform={platform}>
      <ErrorBoundary errorBoundaryKey="seo">
        <BlogPostSEO
          seo={article.seo}
          seoFAQ={article?.SEOFAQ}
          url={pagePathName}
          howToSchema={article.howToSchema}
          articleSchema={article}
        />
      </ErrorBoundary>

      <ErrorBoundary errorBoundaryKey="headerCta">
        <HeaderWithCTA />
      </ErrorBoundary>
      <ErrorBoundary errorBoundaryKey="title">
        <section className={ivclass('iv-mt-[95px] sm:iv-mt-[47px]')}>
          <div className={ivclass('iv-container iv-mx-auto')}>
            <div className={ivclass('iv-grid iv-grid-cols-12 iv-col-span-11')}>
              <div
                className={ivclass(
                  'iv-col-start-2 md:iv-col-start-1',
                  'iv-col-span-10 md:iv-col-span-12 sm:iv-col-span-12',
                )}
              >
                <h1
                  className={ivclass(
                    'iv-text-heading-3xl iv-font-extrabold',
                    'sm:iv-text-heading-s ',
                    'iv-leading-[1]',
                    'iv-px-[23px] md:iv-px-[26px]',
                    'iv-mb-[13px] md:iv-mb-[42px]',
                  )}
                >
                  {title}
                </h1>
                <div
                  className={ivclass('iv-px-20 lg:iv-px-[36px] md:iv-px-25')}
                >
                  <BreadCrumb
                    breadcrumbItems={generateBreadcrumbsFromUrl(
                      pagePathName.replace(/\/?$/, ''),
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </ErrorBoundary>
      <ErrorBoundary errorBoundaryKey="heroImageAndAuthor">
        <section
          className={ivclass(
            'iv-my-[45px]',
            'md:iv-mb-10 sm:iv-mb-10 ',
            'sm:iv-mt-15',
          )}
        >
          <div className={ivclass('iv-container iv-mx-auto')}>
            <div className={ivclass('iv-grid iv-grid-cols-12')}>
              <div
                className={ivclass(
                  'iv-col-span-9 md:iv-col-span-12 sm:iv-col-span-12',
                  'iv-px-20 lg:iv-px-[35px]',
                  'iv-pt-10',
                  'md:iv-mb-40 sm:iv-mb-20',
                )}
              >
                <ImageComponent
                  elementype={featureImage?.url ? 'imagekit' : 'native'}
                  src={featureImage?.url || DEFAULT_LOGO_URL}
                  sizeTransformation={getImageSize(
                    platform,
                    FEATURE_IMAGEKIT_MAP,
                  )}
                  fetchpriority="high"
                  className={ivclass(
                    'iv-rounded-30 sm:iv-rounded-lg',
                    'iv-h-[600px] iv-w-[1040px]',
                    'lg:iv-h-[550px] lg:iv-w-[1040px]',
                    'md:iv-h-[452px] md:iv-w-[1040px]',
                    'sm:iv-h-[184px] sm:iv-w-full',
                    'iv-object-cover',
                  )}
                />
              </div>

              <div
                className={ivclass(
                  'iv-col-span-3 md:iv-col-span-12 sm:iv-col-span-12',
                  'md:iv-flex md:iv-justify-between',
                  'md:iv-px-10',
                )}
              >
                <div
                  className={ivclass(
                    'iv-hidden sm:iv-flex',
                    'iv-px-20',
                    'iv-gap-y-0 iv-gap-x-7',
                  )}
                >
                  <BlogTags
                    category={blogCategory}
                    platform={blogPlatform}
                    profession={blogProfession}
                    textClass={tagTextClass}
                  />
                </div>
                <AuthorDetails
                  author={author}
                  platform={platform}
                  browser={browser}
                  publishedAt={publishedAt}
                />
                <div
                  className={ivclass(
                    'iv-mx-20 lg:iv-mx-10',
                    'iv-mt-20 md:iv-mt-[16px] sm:iv-mt-5',
                  )}
                >
                  <div className={ivclass('md:iv-hidden sm:iv-hidden')}>
                    <Banner
                      hasUserSignedIn={hasUserLoggedIn}
                      platform={platform}
                      browser={browser}
                    />
                  </div>
                  <div
                    className={ivclass(
                      'iv-mb-25 lg:iv-mb-20',
                      'md:iv-hidden sm:iv-hidden',
                    )}
                  >
                    <div
                      className={ivclass(
                        'iv-text-heading-3xs iv-font-extrabold',
                        'iv-mb-20 lg:iv-mb-10',
                      )}
                    >
                      {t('share article')}
                    </div>
                    <ShareOptionsWrapper
                      visibleAtTop
                      postTitle={title}
                      postUrl={`${host_url}${pagePathName}`}
                    />
                  </div>
                  <div
                    className={ivclass(
                      'iv-mb-10 md:iv-mb-[13px]',
                      'iv-flex iv-flex-wrap',
                      'iv-gap-x-10 iv-gap-y-0',
                      'sm:iv-hidden',
                    )}
                  >
                    <BlogTags
                      category={blogCategory}
                      platform={blogPlatform}
                      profession={blogProfession}
                      textClass={tagTextClass}
                    />
                  </div>
                  <div
                    className={ivclass('iv-flex iv-gap-5', 'md:iv-justify-end')}
                  >
                    <ImageComponent
                      elementype="native"
                      loading="lazy"
                      width="12"
                      height="17"
                      src={getAssetUrl('blog', '/icons/clock.svg')}
                    />
                    <div
                      className={ivclass(
                        'iv-text-body-s iv-font-medium',
                        'iv-text-black-75',
                      )}
                    >
                      {calculateReadingTime(blogBody)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </ErrorBoundary>

      <section className={ivclass('iv-mb-100')}>
        <div className={ivclass('iv-grid iv-grid-cols-9')}>
          <ErrorBoundary errorBoundaryKey="blogTOC">
            <div
              className={ivclass(
                'iv-px-20',
                'sm:iv-h-[0]',
                'md:iv-z-[25] sm:iv-z-[25]',
                'sm:iv-sticky sm:iv-top-[calc(100vh-53px)]',
                'md:iv-sticky md:iv-top-[calc(100vh-83px)]',
                'iv-col-span-2 md:iv-col-span-12 sm:iv-col-span-12',
                'iv-mt-[0] lg:iv-mt-[29px] xl:iv-mt-[29px]',
                'iv-pl-50 lg:iv-pl-30 md:iv-pl-[20px] sm:iv-pl-[20px]',
              )}
            >
              <TableOfContent toc={toc} />
            </div>
          </ErrorBoundary>

          <ErrorBoundary errorBoundaryKey="blogBody">
            <div
              className={ivclass(
                'iv-col-span-5 md:iv-col-span-12 sm:iv-col-span-12',
                'iv-max-w-[800px] md:iv-max-w-full sm:iv-max-w-full',
                'iv-mt-[24px]',
                'lg:iv-pl-20',
                'md:iv-px-20',
              )}
            >
              <BlogBody
                blogBody={blogBody}
                contextualCards={contextualCards}
                platform={platform}
              />
              <div
                className={ivclass(
                  'iv-bg-black-4',
                  'iv-rounded-[15px]',
                  'md:iv-mb-25 sm:iv-mb-20',
                  'sm:iv-mx-20',
                )}
              >
                <div
                  className={ivclass(
                    'iv-flex iv-justify-center iv-items-center iv-gap-10',
                    'sm:iv-flex-col',
                    'iv-pt-30 iv-pb-[33px]',
                  )}
                >
                  <div
                    className={ivclass('iv-text-heading-3xs iv-font-extrabold')}
                  >
                    {t('share article')}:
                  </div>
                  <ShareOptionsWrapper
                    postTitle={title}
                    postUrl={`${host_url}${pagePathName}`}
                  />
                </div>
              </div>
              <div
                className={ivclass(
                  'iv-hidden md:iv-block sm:iv-block',
                  'sm:iv-mx-20',
                  'sm:iv-pb-100 md:iv-pb-100',
                )}
              >
                <Banner
                  hasUserSignedIn={hasUserLoggedIn}
                  platform={platform}
                  browser={browser}
                />
              </div>
            </div>
          </ErrorBoundary>
        </div>
      </section>

      <ErrorBoundary errorBoundaryKey="youMayAlsoLike">
        <YouMayAlsoLike
          blogCategory={blogCategory}
          blogPlatform={blogPlatform}
          blogProfession={blogProfession}
          slug={slug}
          platform={platform}
          browser={browser}
        />
      </ErrorBoundary>
    </AppLayout>
  );
};

export default BlogPost;

const addContextualCardHTML = (
  blogBody: string = '',
  contextualCards: ContextualCardType[],
): string => {
  let content = blogBody.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  contextualCards?.forEach(card => {
    content = content.replace(
      `<!--[contextual-card]-[${card.CardId}]-->`,
      renderToString(<ContextualCard card={card} />),
    );
  });
  return content;
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { post } = context.query;

  const lang = context.locale || 'en';
  const article = await fetchPostDetails(
    typeof post === 'string' ? post : '',
    lang,
  );

  if (!article) {
    return {
      notFound: true,
    };
  }

  article.blogBody = addContextualCardHTML(
    article?.blogBody || '',
    article.contextualCard,
  );
  return {
    props: { article },
  };
}
